/**
 * `OutboxStore` — the storage engine under outbox v2 (ATL-1445, write cutover stage 3, plan §2).
 *
 * STORAGE ONLY. No queue policy lives here: no retries, no classification, no draining, no
 * alerting. Those are P3's. This layer's single job is that a write the device accepted is on disk
 * in the order it was accepted, exactly once, until someone explicitly removes it. The v2 master
 * queue is still dark (nothing sends from it until P3); the engine's first user is the Firestore-mode
 * mirror-intent journal (ATL-1459, `global/Firebase/mirrorIntent.ts`), in its own namespace.
 *
 * Two engines behind this one interface, split the way `firebaseFirestore.{native,web}.ts` is:
 *  - `store.native.ts` — expo-file-system, one JSON file per entry.
 *  - `store.web.ts`    — IndexedDB, one row per entry.
 * The same contract suite (`__tests__/outboxStore.contract.test.js`) runs against both.
 *
 * WHY NOT AsyncStorage (the v1 outbox's store): Android caps the whole AsyncStorage database at
 * 6 MB without a native rebuild, and web AsyncStorage is localStorage, which v1 already caps at
 * 2 MB. A master queue may not drop, so it may not have a size cap it can hit in normal trading.
 *
 * ── Contract ──────────────────────────────────────────────────────────────────────────────────
 *
 * ORDER. Entries are one FIFO per organization, ordered by `seq`, which the store assigns on
 * `append` and which strictly increases within an organization for the life of a store instance.
 * `list` returns that order. Organizations never see each other's entries.
 *
 * IDENTITY. `entryId` is unique within an organization. `append` of an id that already exists —
 * including one whose record is corrupt — is refused with `OutboxDuplicateEntryError`, so a caller
 * that crashed mid-copy (the v1 → v2 migration) can re-run without queueing anything twice. The
 * entryId is therefore the dedupe key for every outcome below that is not definite.
 *
 * A DUPLICATE IS "LANDED" ONLY WHEN `existing === 'ok'` (review 2). The refusal says which record
 * holds the id: `'ok'` — a readable entry, the write is queued; `'corrupt'` — a record the store
 * cannot read (an Android kill or a disk-full failure truncated its `.tmp`, a power loss tore it),
 * i.e. the write is NOT recoverable from here. A caller that treats a duplicate as "already landed"
 * and then discards its own copy — the v1 → v2 migration removing the v1 entry, a retry dropping
 * its in-memory request — may do so ONLY when `existing === 'ok'`; on `'corrupt'` it must keep its
 * copy and surface the pair for an operator.
 *
 * DURABILITY. A mutation's promise resolves only after the engine reports it durable: on native,
 * after the rename that publishes the file; on web, after the IndexedDB transaction's `complete`.
 * (Web REQUESTS `durability: 'strict'`; a browser may treat it as a hint.) What an interrupted or
 * failed mutation leaves behind:
 *  - `append` REJECTED with anything other than `OutboxAppendIndeterminateError` → nothing was
 *    queued, now or after any restart. The caller may surface the failure and re-issue.
 *  - `append` REJECTED with `OutboxAppendIndeterminateError` (native only) → the outcome is
 *    UNKNOWN: a publish failed and the store could not prove its `.tmp` gone, so the next recovery
 *    may publish it. The caller must NOT re-issue the write under a fresh entryId — that would
 *    queue it twice and book it twice. Retrying with the SAME entryId is safe, and a duplicate
 *    refusal with `existing === 'ok'` means it is queued (see IDENTITY for `'corrupt'`); after a
 *    restart `get(entryId)` answers.
 *  - `append` interrupted by the process dying → after restart one of: absent; present exactly
 *    once at the seq it was given; or (native, Android only) a `corrupt` `incomplete-write` slot at
 *    that seq carrying that entryId, when the kill truncated the `.tmp` mid-write. Never present
 *    twice. (Native: a fully written `.tmp` whose rename did not happen is PROMOTED, because the
 *    writer had already produced the whole entry and the cashier's cart was already cleared —
 *    invariant 1. Dropping it would lose a sale.)
 *  - `update` interrupted or rejected → the entry is intact, carrying either the old or the new
 *    fields.
 *  - `remove` interrupted → the entry is present or absent; nothing else changes.
 *  - `appendDelete` interrupted or partially failed → either nothing happened, or the delete is
 *    queued and a PREFIX, in seq order, of the entries it was purging is still queued in front of
 *    it: the earliest of them (the create, when one was queued) and zero or more of the updates
 *    that followed it. Native purges in DESCENDING seq order precisely so that what survives is
 *    always such a prefix — never an update without the create before it, which the backend
 *    could not apply. Draining the prefix and then the delete is correct. (Web does the whole
 *    thing in one transaction, so it is all-or-nothing there.)
 * Residual, native: expo-file-system cannot fsync. An APP kill cannot tear a published file, but
 * an OS crash or power loss can leave a published file empty or truncated. That record is then
 * reported corrupt (below), not lost silently.
 *
 * DELETES NEVER ORPHAN A REFERENCE (invariant 5, review S2). `appendDelete` purges the document's
 * earlier `pending` create/update entries of the SAME resource (`resourceOf`) — and only when
 * nothing else in the organization's queue could still need them: no other entry names the
 * document in `relatedIds` (a sale queued behind its client's create), no other entry of any state
 * writes the document, no record in the queue is unreadable (it might be either), and none of the
 * candidates was queued by another install (a restored backup's entry is for an operator to
 * judge, not for this device to discard). Otherwise it purges nothing and the delete simply queues
 * in FIFO order; `purgeWithheld` says why.
 *
 * CORRUPT AND STRAY RECORDS. A record that fails `validateStoredEntry`, or whose content disagrees
 * with where it is stored, is a `corrupt` slot at its position in the queue. A file whose NAME this
 * engine never writes (e.g. an iOS atomic-write leftover) is a `stray` slot with no position, which
 * sorts first. The store never deletes, rewrites or skips either on its own; `get`/`update`/`remove`
 * on a corrupt entry throw `OutboxCorruptEntryError`, and only the explicit `removeCorrupt` — an
 * operator's decision — takes one away. Whether each kind blocks the drain is P3's decision.
 *
 * INSTALL IDENTITY (reviews 1 S3, 2 item 4, 3 A). Every store has an install id and stamps it on
 * every entry it appends. Native derives it from the OS vendor id AND the app's installation time
 * (`store.native.ts` has the per-platform table of which events yield a new id), so a queue
 * restored from a backup — onto another device, a reset one, or the same device after a reinstall
 * (Android Auto Backup) — lists its entries as `foreignInstall`: the old install may already have
 * delivered them. A cache-stored id is only the fallback for a launch where either value is
 * unavailable. `adoptForeign` is the operator's way to vouch for such entries.
 *
 * NO RESURRECTION. `update` of an entry that is not there returns `null` and writes nothing: a
 * late `update` from a drain that raced a `remove` must never bring a delivered write back.
 *
 * ── Obligations this layer places on P3 (the queue) ──────────────────────────────────────────
 *  1. Run `prepareNewEntry` inside `planX`, BEFORE the cart is cleared, so a write the store would
 *     refuse fails while the cashier can still see it (review N6).
 *  2. HOLD every `foreignInstall` entry for an operator; never replay one automatically. A replayed
 *     restored create is idempotent, but a restored update or delete re-applies stale state over
 *     what happened since.
 *  3. NEVER call `adoptForeign` from the drain or any automatic path. It exists for an operator
 *     who has checked the named entries against the books; an automatic caller would turn the
 *     foreign-install hold into a no-op. Emit one telemetry event per adopted entry (entryId,
 *     fromInstallId, actor, reason) — the store records the adoption on the entry, but the entry
 *     is deleted once delivered, so telemetry is the durable audit trail.
 *  4. Treat `OutboxAppendIndeterminateError` as "maybe queued": no re-ring under a fresh id.
 *  5. Treat `OutboxDuplicateEntryError` as "landed" only when `existing === 'ok'`.
 *  6. Decide, per kind, whether `corrupt` and `stray` slots block the drain, and report both.
 *  7. Report `purgeWithheld` and `purgeIncomplete` from `appendDelete`.
 *  8. Never keep the store returned by `openOutboxStore()` across a `close()`: call it again. A
 *     kept reference is the closed instance and refuses every operation.
 */
import {
  adoptionAuditProblem,
  resourceOf,
  type NewOutboxEntry,
  type OutboxAdoptionAudit,
  type OutboxEntryPatch,
  type OutboxEntryV2,
} from './outboxEntry'

export type OutboxEngine = 'file' | 'indexeddb'

export interface OutboxCorruptSlot {
  status: 'corrupt'
  /** Queue position when it can be read from where the record is stored, else `null` (sorts first). */
  seq: number | null
  /** `entryId` when it can be read from where the record is stored. */
  entryId: string | null
  /** Engine-specific handle for `removeCorrupt` (native: file name; web: primary key). */
  ref: string
  reason: string
  /** The stored bytes/row as text, for an operator to repair by hand. Never sent anywhere by the store. */
  raw: string | null
}

/** A file in the store's directories whose name the engine never writes. Native only. */
export interface OutboxStraySlot {
  status: 'stray'
  seq: null
  entryId: null
  /** File name, for `removeCorrupt`. */
  ref: string
  reason: string
  /** File contents when it is a readable file, `null` for a directory. */
  raw: string | null
}

export type OutboxSlot =
  | {
      status: 'ok'
      seq: number
      entry: OutboxEntryV2
      /** Queued by a different install than this store's (a restored backup) — see INSTALL IDENTITY. */
      foreignInstall: boolean
    }
  | OutboxCorruptSlot
  | OutboxStraySlot

export type InstallIdOrigin = 'vendor+install' | 'cache-read' | 'cache-created' | 'existing' | 'created'

export interface AdoptForeignResult {
  /** Re-stamped to this install, with the previous id and time recorded in `adoptions`. */
  adopted: string[]
  /** Already this install's; left untouched. */
  notForeign: string[]
  notFound: string[]
  /** Unreadable records; never rewritten. */
  corrupt: string[]
}

export interface OutboxForeignInstallEntry {
  orgFirestoreId: string
  entryId: string
  seq: number
  installId: string
}

export interface OutboxCensus {
  /** This store's install id. */
  installId: string
  /**
   * Where the install id came from. Native: `vendor+install` (derived from the vendor id and the
   * installation time — the normal case), `cache-read` (either value unavailable this launch; the
   * cached copy of the last derived id was used), `cache-created` (nothing available; a new id was
   * minted — older entries read foreign, and so will this launch's on the next derived launch).
   * Web: `existing` / `created` (the id in the database's `meta` store).
   */
  installIdOrigin: InstallIdOrigin
  /** Organizations with at least one record (valid, corrupt or stray). */
  organizations: string[]
  /** Records that cannot be attributed to any organization; each must be surfaced. */
  unattributed: Array<OutboxCorruptSlot | OutboxStraySlot>
  /** Every readable entry queued by another install, across organizations. */
  foreignInstallEntries: OutboxForeignInstallEntry[]
}

/** Why `appendDelete` purged nothing although earlier writes of the document were queued. */
export type PurgeWithheldReason =
  | 'referenced'
  | 'other-writes-for-document'
  | 'unreadable-records'
  | 'foreign-install'

export interface AppendDeleteResult {
  entry: OutboxEntryV2
  /** The pending entries the delete superseded — returned so the caller can report them. */
  purged: OutboxEntryV2[]
  /** Set when purgeable entries existed but were kept, so nothing could be orphaned. */
  purgeWithheld: PurgeWithheldReason | null
  /**
   * `true` when the delete WAS queued but removing the superseded entries stopped part-way (native
   * only; web is one transaction). Purging runs in DESCENDING seq order, so what is left in front of
   * the delete is exactly a seq-order prefix of the entries it was purging — the earliest (the
   * create, when queued) plus zero or more of the updates after it, never an update without its
   * create. `purged` lists what did go. Draining the prefix and then the delete is correct, so this
   * is not an error.
   */
  purgeIncomplete: boolean
}

export interface OutboxStore {
  readonly engine: OutboxEngine

  /**
   * Queue a write at the tail of its organization's FIFO. Resolves with the stored entry (incl.
   * `seq` and `installId`). See DURABILITY for what each rejection means.
   */
  append(entry: NewOutboxEntry): Promise<OutboxEntryV2>

  /**
   * Invariant 5: queue a DELETE and purge the document's superseded earlier writes — under the
   * rule in DELETES NEVER ORPHAN A REFERENCE. `in-flight` entries are never purged (they may have
   * committed server-side), nor `held` ones (only a person resolves a held entry). Refuses any
   * `kind` other than `'delete'`.
   */
  appendDelete(entry: NewOutboxEntry): Promise<AppendDeleteResult>

  /** The organization's queue in `seq` order, corrupt and stray records included. */
  list(orgFirestoreId: string): Promise<OutboxSlot[]>

  /** `null` when absent; throws `OutboxCorruptEntryError` when the record exists but is unreadable. */
  get(orgFirestoreId: string, entryId: string): Promise<OutboxEntryV2 | null>

  /**
   * Change delivery state (`state`, `attempts`, `lastError`, `heldSince`, `nextAttemptAt` only).
   * Resolves with the updated entry, or `null` — writing nothing — when the entry is absent.
   */
  update(orgFirestoreId: string, entryId: string, patch: OutboxEntryPatch): Promise<OutboxEntryV2 | null>

  /** Remove a delivered (or operator-resolved) entry. `false` when it was already absent. */
  remove(orgFirestoreId: string, entryId: string): Promise<boolean>

  /** Operator-only: remove a record `list`/`census` reported as corrupt or stray, by its `ref`. */
  removeCorrupt(orgFirestoreId: string | null, ref: string): Promise<boolean>

  /**
   * OPERATOR-ONLY, AUDITED: re-stamp the named `foreignInstall` entries to this install, appending
   * `{ fromInstallId, at, actor, reason }` to each one's `adoptions`. For an operator who has
   * checked those entries against the books after a restore. The drain and every other automatic
   * path must NEVER call it, and P3 emits a telemetry event per adoption (P3 obligation 3).
   * Entries already this install's, absent or corrupt are reported, not touched. Refused with
   * `OutboxAdoptionRefusedError` while the install id is cache-derived (`cache-created` or
   * `cache-read`, native): only a derived id makes an adoption stick.
   */
  adoptForeign(
    orgFirestoreId: string,
    entryIds: readonly string[],
    audit: OutboxAdoptionAudit,
  ): Promise<AdoptForeignResult>

  /** Install identity, every organization with records, and records attributable to none. */
  census(): Promise<OutboxCensus>

  close(): Promise<void>
}

export class OutboxValidationError extends Error {
  constructor(public readonly reason: string) {
    super(`outbox entry refused: ${reason}`)
    this.name = 'OutboxValidationError'
  }
}

export class OutboxDuplicateEntryError extends Error {
  constructor(
    public readonly orgFirestoreId: string,
    public readonly entryId: string,
    /**
     * What holds the id: `'ok'` — a readable queued entry (the write has landed); `'corrupt'` — an
     * unreadable record (it has NOT, recoverably). See IDENTITY in the module docblock.
     */
    public readonly existing: 'ok' | 'corrupt',
  ) {
    super(`outbox entry ${entryId} already exists (${existing}) for organization ${orgFirestoreId}`)
    this.name = 'OutboxDuplicateEntryError'
  }
}

export class OutboxCorruptEntryError extends Error {
  constructor(
    public readonly orgFirestoreId: string,
    public readonly entryId: string,
    public readonly reason: string,
  ) {
    super(`outbox entry ${entryId} for organization ${orgFirestoreId} is corrupt: ${reason}`)
    this.name = 'OutboxCorruptEntryError'
  }
}

/**
 * An append whose outcome is UNKNOWN (review S1): its publish failed and the store could not prove
 * the unpublished copy gone, so a later recovery may queue it. Carries the entryId, which is the
 * dedupe key: never re-issue the write under a different one.
 */
export class OutboxAppendIndeterminateError extends Error {
  constructor(
    public readonly orgFirestoreId: string,
    public readonly entryId: string,
    public readonly cause: unknown,
  ) {
    super(
      `outbox append of ${entryId} for organization ${orgFirestoreId} has an unknown outcome: ` +
        (cause instanceof Error ? cause.message : String(cause)),
    )
    this.name = 'OutboxAppendIndeterminateError'
  }
}

/** `adoptForeign` refused as a whole; nothing was re-stamped. */
export class OutboxAdoptionRefusedError extends Error {
  constructor(
    public readonly kind: 'cache-created' | 'cache-read' | 'invalid-audit',
    detail: string,
  ) {
    super(`outbox adoption refused (${kind}): ${detail}`)
    this.name = 'OutboxAdoptionRefusedError'
  }
}

/** Validate the operator's `{ actor, reason }`; returns a detached copy or throws. */
export function checkAdoptionAudit(audit: unknown): OutboxAdoptionAudit {
  const problem = adoptionAuditProblem(audit)
  if (problem) throw new OutboxAdoptionRefusedError('invalid-audit', problem)
  const { actor, reason } = audit as OutboxAdoptionAudit
  return { actor, reason }
}

/** The web store could not open its database — loudly, instead of hanging (review N3). */
export class OutboxStorageUnavailableError extends Error {
  constructor(
    public readonly kind: 'blocked' | 'version' | 'open-failed',
    detail: string,
  ) {
    super(`outbox storage unavailable (${kind}): ${detail}`)
    this.name = 'OutboxStorageUnavailableError'
  }
}

/** Order slots for `list`: unknown position first (it must block), then ascending `seq`. */
export function compareSlots(a: Pick<OutboxSlot, 'seq'>, b: Pick<OutboxSlot, 'seq'>): number {
  if (a.seq === null && b.seq === null) return 0
  if (a.seq === null) return -1
  if (b.seq === null) return 1
  return a.seq - b.seq
}

const PURGEABLE_KINDS: ReadonlySet<string> = new Set(['create', 'update'])

/**
 * The one purge rule both engines apply (DELETES NEVER ORPHAN A REFERENCE). `others` is every
 * record of this organization — the delete's — EXCEPT the delete itself, in any order.
 *
 * `e.seq < deleteEntry.seq` is always true where the engines call this today: the delete has just
 * been appended at the tail, inside the same serialised operation (native) or transaction (web),
 * so nothing later exists. It is kept so the rule stays "earlier writes only" by construction if a
 * caller ever plans against a queue that has grown since.
 */
export function planDeletePurge(
  deleteEntry: OutboxEntryV2,
  others: readonly OutboxSlot[],
): { purge: OutboxEntryV2[]; withheld: PurgeWithheldReason | null } {
  const documentId = deleteEntry.documentId
  const resource = resourceOf(deleteEntry)
  const candidates = new Set<OutboxEntryV2>()
  for (const slot of others) {
    if (slot.status !== 'ok') continue
    const e = slot.entry
    if (
      e.seq < deleteEntry.seq &&
      e.documentId === documentId &&
      resourceOf(e) === resource &&
      e.state === 'pending' &&
      PURGEABLE_KINDS.has(e.kind)
    ) {
      candidates.add(e)
    }
  }
  if (candidates.size === 0) return { purge: [], withheld: null }
  if (others.some((slot) => slot.status !== 'ok')) return { purge: [], withheld: 'unreadable-records' }
  if (
    others.some((slot) => slot.status === 'ok' && slot.foreignInstall && candidates.has(slot.entry))
  ) {
    return { purge: [], withheld: 'foreign-install' }
  }
  const rest = others.flatMap((slot) => (slot.status === 'ok' && !candidates.has(slot.entry) ? [slot.entry] : []))
  if (rest.some((e) => e.documentId === documentId)) return { purge: [], withheld: 'other-writes-for-document' }
  if (rest.some((e) => e.relatedIds.includes(documentId))) return { purge: [], withheld: 'referenced' }
  return { purge: [...candidates], withheld: null }
}

/**
 * A fresh install id. Uniqueness, not secrecy, is the requirement (no crypto module is assumed:
 * Hermes has no `crypto.randomUUID`): 36-bit clock prefix plus ~104 random bits.
 */
export function generateInstallId(random: () => number = Math.random, now: () => number = Date.now): string {
  let tail = ''
  for (let i = 0; i < 4; i++) tail += Math.floor(random() * 2 ** 26).toString(36).padStart(6, '0')
  return `inst-${now().toString(36)}-${tail}`
}

/**
 * `inner`'s operations, each waiting for `gate` first (a previous instance's close; see below). A
 * top-level function with `inner` as its own parameter so the W-writer census can hold it to one
 * reviewed entry (`NON_FIRESTORE_RECEIVERS`): `inner.update` here is the outbox store's, not Firestore's.
 */
function operationsAfter(inner: OutboxStore, gate: Promise<void>): Omit<OutboxStore, 'close'> {
  const after =
    <A extends unknown[], R>(fn: (...args: A) => Promise<R>) =>
    async (...args: A): Promise<R> => {
      await gate
      return fn(...args)
    }
  return {
    engine: inner.engine,
    append: after(inner.append),
    appendDelete: after(inner.appendDelete),
    list: after(inner.list),
    get: after(inner.get),
    update: after(inner.update),
    remove: after(inner.remove),
    removeCorrupt: after(inner.removeCorrupt),
    adoptForeign: after(inner.adoptForeign),
    census: after(inner.census),
  }
}

/**
 * The app's one store per platform, with a close that is safe against a reopen (review 2, item 1).
 *
 * The old instance's queue of operations may still be running when `close()` is called — a
 * `remove` of a delivered entry, an `append` that already chose its seq. A new instance that
 * started at once would read the directory mid-way: it could cache an entry the old one is about
 * to delete and write it back (a delivered write resurrected), or pick the same next seq as the
 * old one's pending append (two files at one seq). So `close()` releases the shared slot at once
 * — the next `open()` must not hand back a closing store — but every operation of the NEXT
 * instance first waits until the previous one has drained and closed.
 *
 * `close()` is memoised per instance (review 3 B): a repeat call — from a holder that kept the
 * instance, or a double close — returns the first call's promise and never touches the chain.
 * Re-registering it would let the next instance skip the wait on a LATER instance's close (two
 * files at one seq). Callers must not keep an instance across `close()` (P3 obligation 8).
 */
export function createSharedStoreOpener(create: () => OutboxStore): { open(): OutboxStore } {
  let shared: OutboxStore | null = null
  let previousClosed: Promise<void> = Promise.resolve()

  function open(): OutboxStore {
    if (shared) return shared
    const gate = previousClosed
    const inner = create()
    let closing: Promise<void> | null = null
    const opened: OutboxStore = {
      ...operationsAfter(inner, gate),
      close() {
        if (closing) return closing
        closing = gate.then(() => inner.close())
        // Registered BEFORE the slot is released, so the next instance waits for this close.
        previousClosed = closing.catch(() => undefined)
        if (shared === opened) shared = null
        return closing
      },
    }
    shared = opened
    return opened
  }

  return { open }
}
